import { Injectable, Injector } from '@angular/core';
import { IsLoadingService } from './is-loading.service';
import { SignerRepository } from '../repository';
import { WalletModalService } from './wallet-modal.service';
import { SentryUserUtil } from '../util';
import { TermsSignService } from './terms-sign.service';
import { WalletService } from './wallet.service';
import { JsonRpcSigner } from 'ethers';

@Injectable({
  providedIn: 'root',
})
export class SignerService {
  public static SET_ADDRESS_TIMEOUT_MILLISECONDS = 500; // needed because chain gets determined based on signer

  private signer: JsonRpcSigner | undefined;
  private currentlyHandlingSigner: JsonRpcSigner | undefined;

  constructor(
    private injector: Injector,
    private isLoadingService: IsLoadingService,
    private signerRepository: SignerRepository,
    private termsSignService: TermsSignService,
    private walletService: WalletService,
    private walletModalService: WalletModalService,
  ) {
    this.setupSubscriptions();
  }

  public async connectSigner(): Promise<void> {
    return this.injector.get(WalletModalService).connect(); // prevent cycling dependency
  }

  private setupSubscriptions(): void {
    this.setupAccountsChangedSubscription();
  }

  private setupAccountsChangedSubscription(): void {
    this.walletModalService.getNewSignerObservable().subscribe(newSigner => {
      void this.handleNewSigner(newSigner);
    });
  }

  public reHandleWalletModalSigner(): void {
    void this.handleNewSigner(this.walletModalService.getNewSignerValue());
  }

  private async handleNewSigner(
    newSigner: JsonRpcSigner | undefined,
  ): Promise<void> {
    if (newSigner && this.signer && this.signer.address == newSigner.address) {
      return Promise.resolve();
    }

    if (
      newSigner &&
      this.currentlyHandlingSigner &&
      this.currentlyHandlingSigner.address == newSigner.address
    ) {
      return Promise.resolve();
    }
    this.currentlyHandlingSigner = newSigner;

    this.isLoadingService.add({ key: 'signer' });

    if (newSigner) {
      const newSignerAddress = await newSigner.getAddress();
      void this.walletService.postConnected(newSignerAddress);
      const termsSigned =
        await this.termsSignService.checkAndSignTerms(newSigner);
      if (termsSigned) {
        this.signer = newSigner;
        this.signerRepository.setConnected(true);
        setTimeout(() => {
          this.signerRepository.setAddress(newSignerAddress);
          SentryUserUtil.setUser(newSignerAddress);
        }, SignerService.SET_ADDRESS_TIMEOUT_MILLISECONDS);
      }
      this.signerRepository.setTermsSigned(termsSigned);
    } else {
      this.signer = undefined;
      this.signerRepository.setConnected(false);
      this.signerRepository.setAddress(undefined);
      SentryUserUtil.setUser();
    }
    this.updateSignerDependantData();

    this.isLoadingService.remove({ key: 'signer' });
    this.currentlyHandlingSigner = undefined;
  }

  private updateSignerDependantData(): void {
    void this.updateTransactionCount();
    void this.updateEthBalance();
  }

  public async updateTransactionCount(): Promise<void> {
    if (this.signer) {
      const count = await this.signer.getNonce();
      if (count !== this.signerRepository.store.getValue().transactionCount) {
        this.signerRepository.setTransactionCount(count);
      }
    } else {
      this.signerRepository.setTransactionCount(undefined);
    }
  }

  public async updateEthBalance(): Promise<void> {
    if (this.signer) {
      const balance: bigint | undefined = await this.signer.provider.getBalance(
        this.signer,
      );
      this.signerRepository.setEthBalance(balance);
    } else {
      this.signerRepository.setEthBalance(undefined);
    }
  }

  public isSignerInitialized(): boolean {
    return this.signer !== undefined;
  }

  public async getSignerConnectIfNotInitialized(): Promise<JsonRpcSigner> {
    if (!this.signer) {
      await this.connectSigner();
    }
    if (!this.signer) {
      throw new Error(`Couldn't connect wallet.`);
    }
    return Promise.resolve(this.signer);
  }

  public getSigner(): JsonRpcSigner | undefined {
    return this.signer;
  }

  public async getEthBalance(): Promise<bigint | undefined> {
    if (!this.signerRepository.store.getValue().ethBalance) {
      await this.updateEthBalance();
    }
    return this.signerRepository.store.getValue().ethBalance;
  }

  public async hasSufficientBalance(requiredAmount: bigint): Promise<boolean> {
    const balance = await this.getEthBalance();
    return balance != undefined && balance - requiredAmount >= 0;
  }

  public async getGasPrice(): Promise<bigint> {
    if (!this.signer) {
      return Promise.resolve(0n);
    }
    const feeData = await this.signer.provider.getFeeData();
    return Promise.resolve(feeData.gasPrice || 0n);
  }

  public async getMaxFeePerGas(): Promise<bigint> {
    if (!this.signer) {
      return Promise.resolve(0n);
    }
    const feeData = await this.signer.provider.getFeeData();
    return Promise.resolve(feeData.maxFeePerGas || 0n);
  }
}
